import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  IconButton,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import { OutreachAutomation, OutreachAutomationType } from "../types";
import ConfigureBentoBrandDatabase from "./Steps/ConfigureBentoBrandDatabase";
import ConfigureGooglePlaces from "./Steps/ConfigureGooglePlaces";
import ConfigureInstagramTrackedCreators from "./Steps/ConfigureInstagramTrackedCreators";
import ConfigureSettings, { SettingsPayload } from "./Steps/ConfigureSettings";
import ConfigureTemplate from "./Steps/ConfigureTemplate";
import SourceSelection from "./Steps/SourceSelection";
import {
  STEP_ORDER,
  STEP_TITLES,
  generateDefaultName,
  getActiveStep,
} from "./schema";

const BASE_PATH = "/dashboard/autopilot/new";

function StepPlaceholder({ label }: { label: string }) {
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography sx={{ fontSize: 14, color: "rgba(1,67,57,0.6)" }}>
        {label} — coming soon.
      </Typography>
    </Box>
  );
}

interface NewAutomationProps {
  onDraftCreated?: (automation: OutreachAutomation) => void;
  onAutomationUpdated?: (automation: OutreachAutomation) => void;
}

export default function NewAutomation({
  onDraftCreated,
  onAutomationUpdated,
}: NewAutomationProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { categoriesById } = useContext(BentoCategoriesContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { draftId: draftIdParam } = useParams<{ draftId: string }>();

  const initialDraftId = draftIdParam ? Number(draftIdParam) : null;

  const [draftId, setDraftId] = useState<number | null>(initialDraftId);
  const [sourceType, setSourceType] = useState<OutreachAutomationType | null>(
    null,
  );
  const [draftName, setDraftName] = useState<string | null>(null);
  const [draftConfig, setDraftConfig] = useState<Record<string, any> | null>(
    null,
  );
  const [draftTemplateIds, setDraftTemplateIds] = useState<number[] | null>(
    null,
  );
  const [draftBrandCount, setDraftBrandCount] = useState<number | null>(null);
  const [draftApprovalMode, setDraftApprovalMode] = useState<
    "auto_schedule" | "approval_required" | null
  >(null);
  const [draftStartDate, setDraftStartDate] = useState<string | null>(null);
  const [draftTimezone, setDraftTimezone] = useState<string | null>(null);
  const [draftLoading, setDraftLoading] = useState(!!initialDraftId);
  const skipNextFetchRef = useRef(false);

  const activeStep = getActiveStep(location.pathname);

  const fetchDraft = async () => {
    if (!currentOrg?.id || !initialDraftId) return;
    setDraftLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${initialDraftId}`,
        "GET",
      );
      const automation: OutreachAutomation = res.outreachAutomation;
      setSourceType(automation.type);
      setDraftName(automation.name);
      setDraftConfig(automation.configuration);
      if (automation.templateIds) {
        setDraftTemplateIds(automation.templateIds);
      }
      setDraftBrandCount(automation.brandCount);
      setDraftApprovalMode(automation.approvalMode);
      setDraftStartDate(automation.startDate);
      if (automation.timezone) {
        setDraftTimezone(automation.timezone);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDraftLoading(false);
    }
  };

  useEffect(() => {
    if (initialDraftId) {
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false;
        setDraftLoading(false);
      } else {
        fetchDraft();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraftId, currentOrg?.id]);

  /** Navigate to a wizard step, embedding the draftId in the path. */
  const navigateToStep = (step: string, id?: number | null) => {
    const effectiveId = id ?? draftId;
    trackEvent("Autopilot New Automation Step Changed", {
      Step: step,
      "Automation Type": sourceType,
    });
    if (step === "source") {
      navigate(`${BASE_PATH}/source`, { replace: true });
    } else {
      navigate(`${BASE_PATH}/${effectiveId}/${step}`, { replace: true });
    }
  };

  const handleSourceNext = async (type: OutreachAutomationType) => {
    if (!currentOrg?.id) return;

    if (draftId) {
      // Type changed on an existing draft — update backend and clear stale local state
      if (type !== sourceType) {
        try {
          const res = await fetcherAuth(
            getToken,
            `/api/organization/${currentOrg.id}/outreach-automations/${draftId}/draft`,
            "PUT",
            {},
            { type },
          );
          onAutomationUpdated?.(res.outreachAutomation);
          setDraftName(null);
          setDraftConfig(null);
        } catch (error) {
          setErrorAlert(error);
          return;
        }
      }
      setSourceType(type);
      skipNextFetchRef.current = true;
      setDraftLoading(true);
      navigateToStep("configure");
      return;
    }

    // No draft yet — create one
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/draft`,
        "POST",
        {},
        { type },
      );
      const newId = res.outreachAutomation.id;
      setDraftId(newId);
      setSourceType(type);
      onDraftCreated?.(res.outreachAutomation);
      skipNextFetchRef.current = true;
      setDraftLoading(true);
      navigateToStep("configure", newId);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleConfigureNext = async (configuration: Record<string, any>) => {
    if (!currentOrg?.id || !draftId || !sourceType) return;
    try {
      // Generate a default name from the config if none has been set yet
      const catIds: string[] =
        configuration?.search_parameters?.category_ids || [];
      const catNames = [
        ...new Set(
          catIds
            .map((id: string) => categoriesById[Number(id)]?.name)
            .filter(Boolean),
        ),
      ];
      const name =
        draftName || generateDefaultName(sourceType, configuration, catNames);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${draftId}/draft`,
        "PUT",
        {},
        { name, configuration },
      );
      onAutomationUpdated?.(res.outreachAutomation);
      setDraftName(name);
      setDraftConfig(configuration);
      navigateToStep("template");
    } catch (error) {
      setErrorAlert(error);
      throw error;
    }
  };

  const handleTemplateNext = async (
    templateIds: number[],
  ): Promise<{ idx: number; message: string } | null> => {
    if (!currentOrg?.id || !draftId) return null;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${draftId}/draft`,
        "PUT",
        {},
        { templateIds },
        true,
      );
      const jsonMessage = await res.json();
      if (res.ok) {
        onAutomationUpdated?.(jsonMessage.outreachAutomation);
        setDraftTemplateIds(templateIds);
        navigateToStep("settings");
        return null;
      }
      // Handle template validation errors from backend
      if (
        jsonMessage.field === "templates" &&
        jsonMessage.templateIdx !== undefined &&
        jsonMessage.templateIdx !== null
      ) {
        return {
          idx: jsonMessage.templateIdx,
          message: jsonMessage.message,
        };
      }
      setAlert(jsonMessage.message, "error");
      // Throw so child resets loading; error is already displayed above
      const err = new Error(jsonMessage.message);
      (err as any)._alerted = true;
      throw err;
    } catch (error) {
      if (!(error as any)?._alerted) {
        setErrorAlert(error);
      }
      throw error;
    }
  };

  const handleSettingsActivate = async (settings: SettingsPayload) => {
    if (!currentOrg?.id || !draftId) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${draftId}/activate`,
        "POST",
        {},
        {
          name: settings.name,
          brandCount: settings.brandCount,
          approvalMode: settings.approvalMode,
          startDate: settings.startDate,
          timezone: settings.timezone,
        },
      );
      onAutomationUpdated?.(res.outreachAutomation);
      setAlert("Automation activated successfully!", "success");
      navigate("/dashboard/autopilot");
    } catch (error) {
      setErrorAlert(error);
      throw error;
    }
  };

  const renderConfigureStep = () => {
    if (sourceType === "bento_database" && draftId) {
      return (
        <ConfigureBentoBrandDatabase
          draftId={draftId}
          onNext={handleConfigureNext}
          initialConfig={draftConfig}
        />
      );
    }
    if (sourceType === "instagram_tracked_creators" && draftId) {
      return (
        <ConfigureInstagramTrackedCreators
          draftId={draftId}
          onNext={handleConfigureNext}
          initialConfig={draftConfig}
        />
      );
    }
    if (sourceType === "google_places" && draftId) {
      return (
        <ConfigureGooglePlaces
          draftId={draftId}
          onNext={handleConfigureNext}
          initialConfig={draftConfig}
        />
      );
    }
    return <StepPlaceholder label="Configure" />;
  };

  return (
    <Box sx={{ py: 4, px: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <IconButton
          onClick={() => navigate("/dashboard/autopilot")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "1px solid rgba(1,67,57,0.15)",
            color: "rgba(1,67,57,0.6)",
            "&:hover": {
              backgroundColor: "rgba(1,67,57,0.05)",
              color: "primary.main",
            },
          }}
        >
          <Box
            component="span"
            className="fa-solid fa-arrow-left"
            sx={{ fontSize: 14 }}
          />
        </IconButton>
        <Typography
          sx={{ fontWeight: 700, fontSize: 22, color: "primary.main" }}
        >
          Create New Automation
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          "& .MuiStepLabel-label": {
            fontSize: 13,
            fontWeight: 600,
            color: "rgba(1,67,57,0.4)",
            "&.Mui-active": { color: "primary.main" },
            "&.Mui-completed": { color: "primary.main" },
          },
          "& .MuiStepIcon-root": {
            color: "rgba(1,67,57,0.15)",
            "&.Mui-active": { color: "primary.main" },
            "&.Mui-completed": { color: "primary.main" },
          },
        }}
      >
        {STEP_ORDER.map((step, index) => {
          const isCompleted = index < activeStep;
          return (
            <Step
              key={step}
              completed={isCompleted}
              sx={{ cursor: isCompleted ? "pointer" : "default" }}
              onClick={() => {
                if (isCompleted) {
                  navigateToStep(step);
                }
              }}
            >
              <StepLabel>{STEP_TITLES[step]}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Step Content */}
      {!draftIdParam ? (
        <SourceSelection onNext={handleSourceNext} initialType={sourceType} />
      ) : draftLoading ? (
        <Box sx={{ maxWidth: 976, mx: "auto" }}>
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ borderRadius: "10px", mb: 2 }}
          />
          <Skeleton
            variant="rounded"
            height={320}
            sx={{ borderRadius: "14px", mb: 2 }}
          />
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ borderRadius: "10px" }}
          />
        </Box>
      ) : (
        <Routes>
          <Route path="configure" element={renderConfigureStep()} />
          <Route
            path="template"
            element={
              draftId ? (
                <ConfigureTemplate
                  draftId={draftId}
                  onNext={handleTemplateNext}
                  initialTemplateIds={draftTemplateIds}
                />
              ) : (
                <StepPlaceholder label="Template" />
              )
            }
          />
          <Route
            path="settings"
            element={
              draftId && sourceType ? (
                <ConfigureSettings
                  draftId={draftId}
                  sourceType={sourceType}
                  onActivate={handleSettingsActivate}
                  onEditStep={navigateToStep}
                  initialBrandCount={draftBrandCount}
                  initialApprovalMode={draftApprovalMode}
                  initialStartDate={draftStartDate}
                  initialTimezone={draftTimezone}
                  draftName={draftName}
                  draftConfig={draftConfig}
                  draftTemplateIds={draftTemplateIds}
                />
              ) : (
                <StepPlaceholder label="Settings" />
              )
            }
          />
          <Route path="*" element={<Navigate to="configure" replace />} />
        </Routes>
      )}
    </Box>
  );
}
